import React, { useState, useEffect, Fragment } from 'react';
import DataTable from 'react-data-table-component';
import API from '../../services/API';
import { Tag } from 'antd';
import {
  Spinner,
  Row,
  Col,
  Input,
  Button,
  Card,
  CardBody,
  CardHeader,
} from 'reactstrap';
import dayjs from 'dayjs';

const AuditTrailTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const fetchLogs = async () => {
    setLoading(true);
    setError('');

    try {
      const params = { limit, offset };

      if (userId) params.user_id = userId;
      if (action) params.action = action;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const token = localStorage.getItem('token');

      const resp = await API.get('/portal/audit/log', {
        params,
        headers: { token },
      });

      if (resp.data && resp.data.code === 1000) {
        const rows = resp.data.data || [];
        setLogs(rows);
        setTotalRows(rows.length || 0);
      } else {
        setError(resp.data.message || 'Gagal mendapatkan log audit');
      }
    } catch (e) {
      console.log(e);
      setError('Ralat semasa mendapatkan log audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [userId, action, startDate, endDate, limit, offset]);

  const handlePageChange = (page) => {
    setOffset((page - 1) * limit);
  };

  const handlePerRowsChange = (newLimit, page) => {
    setLimit(newLimit);
    setOffset((page - 1) * newLimit);
  };

  const handleSearch = () => {
    setOffset(0);
    fetchLogs();
  };

  const handleReset = () => {
    setUserId('');
    setAction('');
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  const columns = [
    {
      name: '#',
      cell: (row, idx) => offset + idx + 1,
      width: '70px',
      center: true,
    },
    {
      name: 'Masa Rekod',
      selector: (row) => dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      sortable: true,
      minWidth: '190px',
      cell: (row) => (
        <div style={{ fontWeight: 700, color: '#0f172a' }}>
          {dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss')}
        </div>
      ),
    },
    {
      name: 'Peranan Pengguna',
      selector: (row) => row.user_role,
      minWidth: '160px',
      cell: (row) => (
        <Tag
          color="geekblue"
          style={{
            borderRadius: '999px',
            fontWeight: 700,
            padding: '3px 9px',
          }}
        >
          {row.user_role || '-'}
        </Tag>
      ),
    },
    {
      name: 'Tindakan',
      selector: (row) => row.action,
      minWidth: '160px',
      cell: (row) => (
        <Tag
          color="blue"
          style={{
            borderRadius: '999px',
            fontWeight: 700,
            padding: '3px 9px',
          }}
        >
          {row.action || '-'}
        </Tag>
      ),
    },
    {
      name: 'Butiran Aktiviti',
      selector: (row) => row.details,
      wrap: true,
      grow: 2,
      cell: (row) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.5,
            color: '#334155',
            fontSize: 13,
          }}
        >
          {row.details || '-'}
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div style={pageStyle}>
        <div style={pageInnerStyle}>
          <div style={heroStyle}>
            <div style={heroGlowStyle} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={heroBadgeStyle}>Keselamatan & Pematuhan</div>

              <h1 style={heroTitleStyle}>Log Audit Jabatan</h1>

              <p style={heroDescriptionStyle}>
                Pantau rekod aktiviti pengguna, perubahan sistem dan tindakan
                penting dalam portal jabatan untuk tujuan semakan dalaman,
                pematuhan dan jejak audit.
              </p>
            </div>
          </div>

          <Card style={cardStyle}>
            <CardHeader style={cardHeaderStyle}>
              <div>
                <div style={cardKickerStyle}>Tapisan Log</div>
                <h5 style={cardTitleStyle}>Carian Rekod Audit</h5>
                <p style={cardSubtitleStyle}>
                  Gunakan tapisan di bawah untuk mencari aktiviti mengikut ID
                  pengguna, tindakan atau julat tarikh.
                </p>
              </div>
            </CardHeader>

            <CardBody style={cardBodyStyle}>
              <Row className="g-3">
                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>ID Pengguna</label>
                  <Input
                    type="text"
                    placeholder="Contoh: user_uuid"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    style={controlStyle}
                  />
                </Col>

                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>Tindakan</label>
                  <Input
                    type="text"
                    placeholder="Contoh: LOGIN"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    style={controlStyle}
                  />
                </Col>

                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>Tarikh Mula</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={controlStyle}
                  />
                </Col>

                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>Tarikh Tamat</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={controlStyle}
                  />
                </Col>

                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>&nbsp;</label>
                  <Button
                    block
                    onClick={handleSearch}
                    disabled={loading}
                    style={primaryButtonStyle}
                  >
                    {loading ? 'Mencari...' : 'Cari Log'}
                  </Button>
                </Col>

                <Col xs={12} sm={6} md={2}>
                  <label style={labelStyle}>&nbsp;</label>
                  <Button
                    block
                    onClick={handleReset}
                    disabled={loading}
                    style={secondaryButtonStyle}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card style={cardStyle}>
            <CardHeader style={cardHeaderStyle}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <div style={cardKickerStyle}>Senarai Aktiviti</div>
                  <h5 style={cardTitleStyle}>Rekod Audit Sistem</h5>
                  <p style={cardSubtitleStyle}>
                    Senarai ini menunjukkan aktiviti yang direkodkan oleh sistem
                    berdasarkan tapisan yang dipilih.
                  </p>
                </div>

                <div style={summaryPillStyle}>
                  Jumlah Rekod: <strong>{logs.length}</strong>
                </div>
              </div>
            </CardHeader>

            <CardBody style={cardBodyStyle}>
              {loading && (
                <div style={loadingBoxStyle}>
                  <Spinner color="primary" />
                  <span>Sedang memuatkan log audit...</span>
                </div>
              )}

              {error && (
                <div
                  className="alert alert-danger"
                  style={{
                    borderRadius: 14,
                    fontWeight: 600,
                  }}
                >
                  {error}
                </div>
              )}

              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={logs}
                  progressPending={loading}
                  striped={false}
                  persistTableHead
                  noHeader
                  pagination
                  paginationServer
                  paginationPerPage={limit}
                  paginationTotalRows={totalRows}
                  paginationDefaultPage={offset / limit + 1}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  highlightOnHover
                  pointerOnHover
                  responsive
                  customStyles={blueDataTableStyles}
                  noDataComponent={
                    <div style={emptyStateStyle}>
                      Tiada rekod audit dijumpai untuk tapisan ini.
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </Fragment>
  );
};

export default AuditTrailTable;

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)',
  padding: '28px 24px 44px',
};

const pageInnerStyle = {
  maxWidth: 1180,
  margin: '0 auto',
};

const heroStyle = {
  background: 'linear-gradient(135deg, #0B4C9C 0%, #0F65C9 55%, #1E88E5 100%)',
  borderRadius: 28,
  padding: '34px 36px',
  marginBottom: 24,
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 24px 70px rgba(11, 76, 156, 0.22)',
};

const heroGlowStyle = {
  position: 'absolute',
  right: -80,
  top: -80,
  width: 260,
  height: 260,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.11)',
};

const heroBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: 999,
  padding: '8px 13px',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 14,
};

const heroTitleStyle = {
  fontSize: 'clamp(30px, 4vw, 48px)',
  fontWeight: 950,
  letterSpacing: -1.4,
  margin: 0,
};

const heroDescriptionStyle = {
  maxWidth: 780,
  margin: '10px 0 0',
  color: 'rgba(255,255,255,0.82)',
  fontSize: 15,
  lineHeight: 1.7,
};

const cardStyle = {
  border: '1px solid #dbeafe',
  borderRadius: 22,
  boxShadow: '0 16px 40px rgba(11, 76, 156, 0.08)',
  overflow: 'hidden',
  background: '#ffffff',
  marginBottom: 22,
};

const cardHeaderStyle = {
  background: '#ffffff',
  borderBottom: '1px solid #dbeafe',
  padding: '20px 24px',
};

const cardKickerStyle = {
  display: 'inline-flex',
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  color: '#0B4C9C',
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: 12,
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};

const cardTitleStyle = {
  color: '#0f172a',
  margin: 0,
  fontWeight: 900,
};

const cardSubtitleStyle = {
  margin: '6px 0 0',
  color: '#64748b',
  fontSize: 14,
  lineHeight: 1.6,
};

const cardBodyStyle = {
  backgroundColor: '#ffffff',
  padding: 24,
};

const labelStyle = {
  color: '#334155',
  fontWeight: 800,
  marginBottom: 6,
  display: 'block',
};

const controlStyle = {
  borderRadius: 14,
  minHeight: 46,
  borderColor: '#bfdbfe',
  boxShadow: 'none',
};

const primaryButtonStyle = {
  backgroundColor: '#0B4C9C',
  color: '#ffffff',
  border: 'none',
  borderRadius: 14,
  fontWeight: 800,
  minHeight: 46,
  padding: '10px 16px',
};

const secondaryButtonStyle = {
  backgroundColor: '#ffffff',
  color: '#0B4C9C',
  border: '1px solid #0B4C9C',
  borderRadius: 14,
  fontWeight: 800,
  minHeight: 46,
  padding: '10px 16px',
};

const summaryPillStyle = {
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  color: '#0B4C9C',
  borderRadius: 999,
  padding: '9px 14px',
  fontSize: 13,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const loadingBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  color: '#64748b',
  fontWeight: 700,
  marginBottom: 16,
};

const emptyStateStyle = {
  padding: '36px 16px',
  color: '#64748b',
  fontWeight: 600,
};

const blueDataTableStyles = {
  table: {
    style: {
      borderRadius: '18px',
      overflow: 'hidden',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#eff6ff',
      borderBottomColor: '#bfdbfe',
      minHeight: '56px',
    },
  },
  headCells: {
    style: {
      color: '#0B4C9C',
      fontSize: '12px',
      fontWeight: 900,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
  },
  rows: {
    style: {
      minHeight: '64px',
      borderBottomColor: '#e5eefb',
      fontSize: '14px',
      color: '#0f172a',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#eff6ff',
    },
  },
  pagination: {
    style: {
      borderTopColor: '#dbeafe',
      color: '#334155',
    },
  },
};